import moment from 'moment';
import { Inject } from '@odinjs/odin';

import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { BaseModalProps, ButtonAPI, FormAPI, InputAPI, ModalButton, ModalProps, ModalService, TextAPI } from '@philips-internal/oswald-ui-components';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { ModalButtonType, ModalType } from '@philips-internal/oswald-ui-constants';
import { AbortableCloseEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';

import { MedicineAndSolutionsParams } from '../../medicine-and-solutions/medicine-and-solutions-params';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import BaseController from '../../../../BaseController';
import { FragmentNames, NursingInterventionsStartAliases } from '../../../../../codegen';
import timeService from '../../../../../services/time-service';
import TimelineContext, { TimeLineEventMedication } from '../../../services/timeline-service';
import { ADEP_E1113Parameters, ADEP_E9129Parameters, FunctionName } from '../../../../../utils/parameters';
import { FoundationUtils } from '../../../../../utils/foundation-utils';
import { AdminModalsService } from '../../../services/admin-modals-service';
import { ApplicationRoute } from '../../../../../constants/routes';
import ExecuteNursingInterventionsService from './services/start-nursing-interventions-service';
import { YesNoType } from '../../../../../utils/enum-utils';
import { Value } from '@philips-internal/oswald-ui-core';
import { BooleanUtils } from '../../../../../utils/boolean-utils';
import { NursingInterventionSchedule } from '../nursing-interventions-records/nursing-interventions-records-type';
import { SavedRecordsContext } from '../nursing-interventions-records/services/saved-records-context';

@Controller({ component: NursingInterventionsStartAliases.NURSING_INTERVENTIONS_START_LAYOUT, fragment: FragmentNames.NURSING_INTERVENTIONS_START })
export default class NursingInterventionsStartController extends BaseController {

  @Inject(NursingInterventionsStartAliases.INFORMATION_FORM)
  private formApi!: FormAPI;

  @Inject(NursingInterventionsStartAliases.BUTTON_EXECUTE)
  private buttonApi!: ButtonAPI

  @OnMount(NursingInterventionsStartAliases.DATE_HOUR_INPUT)
  handleMountDateHourInput({ target }: EventData<InputAPI>): void {
    timeService.getCurrentDateTime()
      .then(date => target.setValue(date))
      .catch(() => target.setValue(moment().format(ISO_FULL_DATE_FORMAT)))
  }

  @OnMount([NursingInterventionsStartAliases.TEXT_NURSING_INTERVENTIONS_DATE, NursingInterventionsStartAliases.TEXT_NURSING_INTERVENTIONS_INFORMATION])
  handleMountTexts({ target }: EventData<TextAPI>): void {
    const data = TimelineContext.get() as TimeLineEventMedication;
    const value = target.getValue()
      .replace(':nursing-interventions-information', data.description as string)
      .replace(':nursing-interventions-date', DateFormatterUtil.timeStamp(String(data.start)));

    target.setValue(value);
  }

  @OnClick(NursingInterventionsStartAliases.BUTTON_EXECUTE)
  handleSubmitSuspendItemForm(): void {
    const nursingInterventions = this.context.get('nursingInterventions');
    const vitalSignsCount = nursingInterventions?.vitalSignsQt ?? 0;
    const formRecord = this.formApi.getRecord();
    const alterationDate = formRecord.currentTime;
    const justifyDescription = formRecord.justification
    const observationDescription = formRecord.observation

    this.context.set({ alterationDate, justifyDescription, observationDescription })

    const mustAdministerAllSAE = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.WHEN_ADMINISTERING_THE_SNCP_ASK_WHETHER_TO_ADMINISTER_ALL_AT_THE_SAME_TIME_SLOT);
    if (mustAdministerAllSAE !== YesNoType.NO) {
      this.openSelectOngoingNursingIntervention()
      return
    }

    const isRequiredEmarRecords = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.RECORD_VITAL_SIGNS_WHEN_ADMINISTERING_AN_SNCP_INTERVENTION);
    if (BooleanUtils.isTrueValue(String(isRequiredEmarRecords)) && Number(vitalSignsCount) > 0) {
      const data = TimelineContext.get() as TimeLineEventMedication;
      const intervention = {
        description: data.description,
        id: Number(MedicineAndSolutionsParams.params.cdMaterialId),
        itemCode: Number(MedicineAndSolutionsParams.params.cdMaterialId),
        itemSequence: Number(MedicineAndSolutionsParams.params.nrSequenceId),
        prescriptionNumber: Number(MedicineAndSolutionsParams.params.nrPrescriptionId),
        scheduleSequence: Number(MedicineAndSolutionsParams.params.hourSequence),
      }
      this.openEmarRecordsForms(intervention)
      return
    }

    this.executeNursingIntevention()
  }

  private executeNursingIntevention(): void {
    const eventDate = moment(MedicineAndSolutionsParams.params.startId).utc().format(ISO_FULL_DATE_FORMAT);

    const requestBody: Record<string, Value> = {
      alterationDate: this.context.get('alterationDate'),
      encounterNumber: MedicineAndSolutionsParams.params.encounterId,
      eventDate,
      hourSequence: MedicineAndSolutionsParams.params.hourSequence,
      intervalCode: MedicineAndSolutionsParams.params.cdIntervalId,
      itemCode: MedicineAndSolutionsParams.params.cdMaterialId,
      itemSequence: MedicineAndSolutionsParams.params.nrSequenceId,
      itemType: MedicineAndSolutionsParams.params.ieTypeItem,
      justifyDescription: this.context.get('justifyDescription') || '',
      observationDescription: this.context.get('observationDescription') || '',
      prescriptionAdministrationNumber: MedicineAndSolutionsParams.params.nrPrescriptionId,
      prescriptionNumber: MedicineAndSolutionsParams.params.nrPrescriptionId,
      scheduleSequence: MedicineAndSolutionsParams.params.hourSequence,
    } as Record<string, Value>;

    this.buttonApi.setEnabled(false)
    this.formApi.setLoading(true)

    ExecuteNursingInterventionsService
      .execute(requestBody)
      .then(() => {
        this.redirectToTimeline()
      })
      .catch(() => {
        this.formApi.setLoading(false)
      }).finally(() => {
        this.buttonApi.setEnabled(true)
      })
  }

  private openEmarRecordsForms(intervention?: NursingInterventionSchedule): void {
    const title = MessageService.translate('text.sncp-care-plan');
    const nursingInterventions = this.context.getBulkData();
    const startNursingInterventionsFragment = FragmentNames.NURSING_INTERVENTIONS_EMAR_RECORDS
    const id = `${startNursingInterventionsFragment}-${intervention?.id}`

    const dialogModal: BaseModalProps = {
      contextData: { currentIntervention: intervention, nursingInterventions },
      focusOnOpen: false,
      fragment: startNursingInterventionsFragment,
      id,
      onBeforeClose: (event) => {
        this.openDialogModalConfirm(event, startNursingInterventionsFragment)
      },
      onClose: () => {
        this.redirectToTimeline()
        SavedRecordsContext.clean()
      },
      title,
      type: ModalType.DEFAULT,
    }

    ModalService.openDefault(dialogModal);
  }

  private openSelectOngoingNursingIntervention(): void {
    const emarRecordsTitleExpression = MessageService.translate('text.sncp-care-plan');
    const nursingInterventions = this.context.getBulkData();
    const selectOngoingNursingInterventionFragment = FragmentNames.SELECT_ONGOING_NURSING_INTERVENTIONS
    const dialogModal: BaseModalProps = {
      contextData: { nursingInterventions: nursingInterventions },
      focusOnOpen: false,
      fragment: selectOngoingNursingInterventionFragment,
      id: selectOngoingNursingInterventionFragment,
      onBeforeClose: (event) => {
        this.formApi.setLoading(false)
        this.buttonApi.setEnabled(true)
        this.openDialogModalConfirm(event, selectOngoingNursingInterventionFragment)
      },
      title: emarRecordsTitleExpression,
      type: ModalType.DEFAULT,
    };

    ModalService.openDefault(dialogModal);
  }

  private openDialogModalConfirm(event: AbortableCloseEvent, fragment: FragmentNames): void {
    event.abort();

    const buttons: ModalButton[] = [];

    buttons.push({
      action: () => { },
      title: 'tws.biometry.cancel',
      type: ModalButtonType.CANCEL,
    });

    buttons.push({
      action: () => ModalService.close(fragment),
      title: 'tws.file-uploader-modal.confirm',
      type: ModalButtonType.SUCCESS,
    });

    const dialogModal: ModalProps = {
      closeByDocument: false,
      closeByEscape: false,
      dialogDescription: MessageService.translate('text.reset items-want-to-continue'),
      dockButtons: buttons,
      focusOnOpen: true,
      title: 'text.message-alert-attention',
      uuid: generateUUID(),
    };

    ModalService.openDialog(dialogModal);
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    AdminModalsService.redirectToTimeline(FragmentNames.NURSING_INTERVENTIONS_START);
    AdminModalsService.redirectToTimeline(FragmentNames.NURSING_INTERVENTIONS_VIEWER);
  }
}